import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { LOGIN_MUTATION, ME_QUERY } from '../apollo/queries';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // GraphQL 뮤테이션 및 쿼리
  const [loginMutation] = useMutation(LOGIN_MUTATION);
  const { data: meData, loading: meLoading, error: meError, refetch: refetchMe } = useQuery(ME_QUERY, {
    skip: !localStorage.getItem('authToken'),
    errorPolicy: 'ignore',
    fetchPolicy: 'cache-and-network'
  });

  // 로딩 타임아웃 설정 (10초 후 강제 종료)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading && meLoading) {
        console.warn('AuthContext: ME_QUERY 타임아웃, 로딩을 종료합니다.');
        setLoading(false);
        if (localStorage.getItem('authToken')) {
          // 타임아웃 시 토큰이 손상되었을 가능성이 있으므로 제거
          localStorage.removeItem('authToken');
        }
        setUser(null);
      }
    }, 10000); // 10초

    return () => clearTimeout(timeout);
  }, [loading, meLoading]);

  // 토큰이 있으면 사용자 정보 로드
  useEffect(() => {
    console.log('AuthContext: meData:', meData, 'meError:', meError, 'meLoading:', meLoading);
    
    if (meData && meData.me) {
      setUser(meData.me);
      setLoading(false);
    } else if (meData && meData.me === null) {
      // ME 쿼리는 성공했지만 사용자가 null인 경우 (토큰이 유효하지 않음)
      // 단, 방금 로그인으로 user가 이미 설정된 경우에는 refetch 완료 전의 이전 결과일 수 있으므로 토큰 제거하지 않음
      if (!user) {
        console.warn('AuthContext: ME 쿼리에서 사용자가 null 반환, 토큰을 제거합니다.');
        localStorage.removeItem('authToken');
        setUser(null);
      }
      setLoading(false);
    } else if (!localStorage.getItem('authToken')) {
      setUser(null);
      setLoading(false);
    } else if (meError) {
      // ME 쿼리 에러 발생 시 토큰 제거하고 로딩 종료
      console.error('AuthContext: ME_QUERY 에러:', meError);
      localStorage.removeItem('authToken');
      setUser(null);
      setLoading(false);
    } else if (!meLoading && localStorage.getItem('authToken') && !meData) {
      // 로딩이 끝났는데 데이터가 없으면 토큰이 유효하지 않음
      console.warn('AuthContext: 토큰이 유효하지 않습니다.');
      localStorage.removeItem('authToken');
      setUser(null);
      setLoading(false);
    }
  }, [meData, meError, meLoading, user]);

  // 로그인 함수
  const login = async (loginData) => {
    try {
      const { data } = await loginMutation({
        variables: {
          input: loginData
        }
      });

      if (data && data.login) {
        const { token, user: userData } = data.login;
        
        // 토큰 저장
        localStorage.setItem('authToken', token);
        
        // 사용자 정보 설정
        setUser(userData);
        
        // ME 쿼리 재실행
        refetchMe();
        
        return { success: true, user: userData };
      }
    } catch (error) {
      console.error('로그인 오류:', error);
      
      let errorMessage = '로그인 중 오류가 발생했습니다.';
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error.networkError) {
        errorMessage = '네트워크 오류가 발생했습니다.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // 로그아웃 함수
  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    // Apollo Client 캐시도 클리어
    window.location.reload();
  };

  // 사용자 정보 업데이트
  const updateUser = (userData) => {
    setUser(userData);
  };

  // 로그인 상태 확인
  const isAuthenticated = () => {
    // 토큰이 있고 사용자 정보가 있거나, 토큰만 있어도 로그인된 것으로 간주
    // (사용자 정보는 ME_QUERY로 비동기적으로 로드됨)
    return !!localStorage.getItem('authToken');
  };

  // 관리자 권한 확인
  const isAdmin = () => {
    // 사용자 정보가 로드되지 않은 경우 토큰만으로는 권한 확인 불가
    if (!user) {
      return false;
    }
    return user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';
  };

  const value = {
    user,
    loading: loading || meLoading,
    login,
    logout,
    updateUser,
    isAuthenticated,
    isAdmin,
    refetchMe
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth는 AuthProvider 내부에서 사용되어야 합니다.');
  }
  return context;
}; 